import styled from '@emotion/styled'
import { useEffectOnce } from 'react-use'

import { isEmpty } from '@livechat/data-utils'

import { usePreviewController } from '../../../components/WidgetPreview/previewController'
import Badge from 'components/Badge'
import BottomShade from 'components/BottomShade'
import Button from 'components/Button'
import FeatureLink from 'components/FeatureLink'
import FeatureToggle from 'components/FeatureToggle'
import Loader from 'components/Loader'
import SlideInWrapper from 'components/SlideInWrapper'
import StatusBanner from 'components/StatusBanner'
import UseAsyncFn from 'components/UseAsyncFn'
import { syncStoreWithQuestionsForm } from 'components/forms/QuestionsForm'
import {
	AppsLogoIcon,
	CarouselIcon,
	ChatBotLogoIcon,
	ChatIcon,
	GiftIcon,
	HelpDeskLogoIcon,
	LinksIcon,
	LiveChatLogoIcon,
	MessengerIcon,
	QuestionIcon,
	StarIcon,
	UsersIcon,
	WhatsAppIcon,
} from 'components/icons'
import { useFeature } from 'hooks/useFeature'
import { useIntegration } from 'hooks/useIntegration'
import useSectionOpenedTracking from 'hooks/useSectionOpenedTracking'
import { useTemplate } from 'hooks/useTemplate'
import { useToast } from 'hooks/useToast'
import { useUser } from 'hooks/useUser'
import { updateFeature, updateTemplate } from 'lib/api'
import { useAppStore } from 'lib/store'

const Wrapper = styled.div`
	display: flex;
	flex-direction: column;
	width: 100%;
`

const Section = styled.div`
	position: relative;
	display: flex;
	width: 100%;
	flex-direction: column;
	gap: ${({ theme }) => theme.spaces.space3};
	margin-bottom: ${({ theme }) => theme.spaces.space7};
`

const SectionHeading = styled.h2`
	${({ theme }) => theme.typography.captionMedium};
	color: ${({ theme }) => theme.colors.light.alternativeTextColor};
	margin-bottom: ${({ theme }) => theme.spaces.space3};
`

const EnabledBadge = styled(Badge)<{ status: 'active' | 'expired' }>`
	background-color: ${({ theme, status }) =>
		status === 'active' ? theme.colors.light.desaturetedGreen : theme.colors.light.warningSurface};
	border-radius: ${({ theme }) => theme.borderRadiuses.sm};
	padding: ${({ theme }) => theme.spaces.space1} ${({ theme }) => theme.spaces.space3};
	margin-right: ${({ theme }) => theme.spaces.space3};
`

const ChatOnlyModeEnabledOverlay = styled.div`
	position: absolute;
	inset: 0;
	background: linear-gradient(180deg, rgba(250, 250, 250, 0.4) 0%, rgba(250, 250, 250) 100%);
	z-index: ${({ theme }) => theme.zIndexes.base};
	display: flex;
	justify-content: center;
	align-items: flex-end;
`

const EnableAllFeaturesButton = styled(Button)`
	margin-top: ${({ theme }) => theme.spaces.space4};
`

function WidgetContentIndex() {
	const store = useAppStore()
	const user = useUser()
	const {
		feature: visitorCounter,
		isFeatureLoading: isVisitorCounterLoading,
		mutateFeature: mutateVisitorCounter,
	} = useFeature('visitor_counter')
	const {
		feature: productRecommendations,
		isFeatureLoading: areProductRecommendationsLoading,
		mutateFeature: mutateProductRecommendations,
	} = useFeature('product_recommendations')
	const {
		feature: googleReviewsFeature,
		isFeatureLoading: isGoogleReviewsFeature,
		mutateFeature: mutateGoogleReviewsFeature,
	} = useFeature('google_reviews')
	const {
		feature: whatsappFeature,
		isFeatureLoading: isWhatsAppFeatureLoading,
		mutateFeature: mutateWhatsAppFeature,
	} = useFeature('whatsapp')
	const {
		feature: messengerFeature,
		isFeatureLoading: isMessengerFeatureLoading,
		mutateFeature: mutateMessengerFeature,
	} = useFeature('messenger')
	const { feature: faq, isFeatureLoading: isFAQLoading, mutateFeature: mutateFAQ } = useFeature('faq')
	const { integration: helpDeskIntegration } = useIntegration('helpdesk')
	const { integration: liveChatIntegration } = useIntegration('livechat')
	const { integration: chatbotIntegration } = useIntegration('chatbot')
	const { template, mutateTemplate } = useTemplate()
	const isChatOnlyModeEnabled = template?.isChatOnlyModeEnabled ?? false
	const showToast = useToast()
	const previewController = usePreviewController()
	useSectionOpenedTracking()

	useEffectOnce(() => {
		previewController.showView('Homescreen')
	})

	if (
		isVisitorCounterLoading ||
		areProductRecommendationsLoading ||
		isGoogleReviewsFeature ||
		isFAQLoading ||
		isWhatsAppFeatureLoading ||
		isMessengerFeatureLoading
	) {
		return <Loader delay={0.1} />
	}

	const handleFAQSwitchChange = async (isEnabled: boolean): Promise<{ isEnabled: boolean }> => {
		try {
			const updatedFeature = await updateFeature('faq', { enabled: isEnabled })
			mutateFAQ(updatedFeature, false)
			syncStoreWithQuestionsForm(store, updatedFeature.properties, isEnabled)

			previewController.showView('Homescreen', 'accordion-card')
			showToast('success')
			return { isEnabled }
		} catch {
			showToast('error')
			return { isEnabled: !isEnabled }
		}
	}

	const handleVisitorCounterSwitchChange = async (isEnabled: boolean): Promise<{ isEnabled: boolean }> => {
		store.dispatch({ type: 'visitorCounter/set', payload: { enabled: isEnabled } })
		try {
			await updateFeature('visitor_counter', { enabled: isEnabled })
			mutateVisitorCounter({ name: 'visitor_counter', properties: {}, enabled: isEnabled }, false)
			showToast('success')
			return { isEnabled }
		} catch {
			showToast('error')
			return { isEnabled: !isEnabled }
		}
	}

	const handleProductRecommendationsSwitchChange = async (isEnabled: boolean): Promise<{ isEnabled: boolean }> => {
		if (!productRecommendations?.properties) {
			return { isEnabled: false }
		}

		store.dispatch({ type: 'productRecommendations/set', payload: { enabled: isEnabled } })
		try {
			await updateFeature('product_recommendations', { enabled: isEnabled })
			mutateProductRecommendations(
				{
					name: 'product_recommendations',
					properties: productRecommendations.properties,
					externalProperties: productRecommendations.externalProperties,
					enabled: isEnabled,
				},
				false,
			)
			showToast('success')
			return { isEnabled }
		} catch {
			showToast('error')
			return { isEnabled: !isEnabled }
		}
	}

	async function handleGoogleReviewsSwitchChange(isEnabled: boolean) {
		if (!googleReviewsFeature?.properties) {
			return { isEnabled: false }
		}
		try {
			await updateFeature('google_reviews', { enabled: isEnabled })
			mutateGoogleReviewsFeature(
				{
					name: 'google_reviews',
					createdAt: googleReviewsFeature.createdAt,
					updatedAt: googleReviewsFeature.updatedAt,
					properties: googleReviewsFeature.properties,
					enabled: isEnabled,
				},
				false,
			)
			showToast('success')
			return { isEnabled }
		} catch {
			showToast('error')
			return { isEnabled: !isEnabled }
		}
	}

	async function handleWhatsAppSwitchChange(isEnabled: boolean) {
		previewController.showView('Contact')
		store.dispatch({ type: 'whatsapp/set', payload: { enabled: isEnabled } })
		try {
			await updateFeature('whatsapp', { enabled: isEnabled })
			mutateWhatsAppFeature()
			showToast('success')
			return { isEnabled }
		} catch {
			showToast('error')
			return { isEnabled: !isEnabled }
		}
	}

	async function handleMessengerSwitchChange(isEnabled: boolean) {
		previewController.showView('Contact')
		store.dispatch({ type: 'messenger/set', payload: { enabled: isEnabled } })
		try {
			const updatedFeature = await updateFeature('messenger', { enabled: isEnabled })
			mutateMessengerFeature(updatedFeature)
			showToast('success')
			return { isEnabled }
		} catch {
			showToast('error')
			return { isEnabled: !isEnabled }
		}
	}

	async function handleEnableAllFeatures() {
		try {
			const updatedTemplate = await updateTemplate(user.organizationId, { isChatOnlyModeEnabled: false })
			mutateTemplate(updatedTemplate)
			store.dispatch({ type: 'isChatOnlyModeEnabled/set', payload: false })
			showToast('success')
		} catch {
			showToast('error')
		}
	}

	return (
		<SlideInWrapper axis="y" reversed>
			<Wrapper>
				<Section>
					{isChatOnlyModeEnabled && (
						<ChatOnlyModeEnabledOverlay>
							<StatusBanner type="warning" isVisible={true} title="Chat-Only mode active">
								You can only manage chat-related settings. Other features are locked due to content visibility settings.
								<UseAsyncFn key="enable-all-features" asyncFn={handleEnableAllFeatures}>
									{({ loading, callAsyncFn }) => (
										<EnableAllFeaturesButton
											type="button"
											kind="primary"
											loading={loading}
											onClick={callAsyncFn}
											mediumDesktop
											disableHover
										>
											Enable all features
										</EnableAllFeaturesButton>
									)}
								</UseAsyncFn>
							</StatusBanner>
						</ChatOnlyModeEnabledOverlay>
					)}
					<SectionHeading>Provide help</SectionHeading>
					<FeatureLink href="/configurator/widget-content/contact" icon={<ChatIcon />} disabled={isChatOnlyModeEnabled}>
						Contact card
					</FeatureLink>
					<FeatureLink
						href="/configurator/widget-content/whatsapp"
						icon={<WhatsAppIcon />}
						rightAdornment={
							whatsappFeature?.properties &&
							!isEmpty(whatsappFeature?.properties) && (
								<FeatureToggle
									compact
									ariaName="whatsapp"
									trackingName="WhatsApp"
									isEnabled={!!whatsappFeature?.enabled}
									onChange={handleWhatsAppSwitchChange}
								/>
							)
						}
						disabled={isChatOnlyModeEnabled}
					>
						WhatsApp
					</FeatureLink>
					<FeatureLink
						href="/configurator/widget-content/messenger"
						icon={<MessengerIcon />}
						rightAdornment={
							messengerFeature?.properties &&
							!isEmpty(messengerFeature?.properties) && (
								<FeatureToggle
									compact
									ariaName="messenger"
									trackingName="Messenger"
									isEnabled={!!messengerFeature?.enabled}
									onChange={handleMessengerSwitchChange}
								/>
							)
						}
						disabled={isChatOnlyModeEnabled}
					>
						Messenger
					</FeatureLink>
					<FeatureLink
						href="/configurator/widget-content/faq"
						icon={<QuestionIcon size={20} />}
						rightAdornment={
							<FeatureToggle
								isEnabled={!!faq?.enabled}
								onChange={handleFAQSwitchChange}
								ariaName="faq"
								trackingName="FAQ"
								compact
							/>
						}
						disabled={isChatOnlyModeEnabled}
					>
						FAQ
					</FeatureLink>
					<FeatureLink
						href="/configurator/widget-content/custom-links"
						icon={<LinksIcon size={20} />}
						disabled={isChatOnlyModeEnabled}
					>
						Custom links
					</FeatureLink>
				</Section>

				{!isChatOnlyModeEnabled && (
					<Section>
						<SectionHeading>Boost sales</SectionHeading>
						<FeatureLink href="/configurator/widget-content/product" icon={<CarouselIcon />}>
							Product carousel
						</FeatureLink>
						<FeatureLink
							href="/configurator/widget-content/product-recommendations"
							icon={<GiftIcon />}
							rightAdornment={
								productRecommendations?.properties.website &&
								productRecommendations?.externalProperties.foundProductsCount ? (
									<FeatureToggle
										isEnabled={!!productRecommendations?.enabled}
										onChange={handleProductRecommendationsSwitchChange}
										ariaName="product recommendations"
										trackingName="Product recommendations"
										compact
									/>
								) : null
							}
						>
							Product recommendations
						</FeatureLink>
					</Section>
				)}

				{!isChatOnlyModeEnabled && (
					<Section>
						<SectionHeading>Build trust</SectionHeading>
						<FeatureLink
							href="/configurator/widget-content/visitor-counter"
							icon={<UsersIcon />}
							rightAdornment={
								<FeatureToggle
									isEnabled={!!visitorCounter?.enabled}
									onChange={handleVisitorCounterSwitchChange}
									onClick={() => previewController.showView('Homescreen', 'visitor-counter-card')}
									ariaName="visitor counter"
									trackingName="Visitor counter"
									compact
								/>
							}
						>
							Visitor counter
						</FeatureLink>
						<FeatureLink
							href="/configurator/widget-content/google-reviews"
							icon={<StarIcon />}
							dataTestId="google-reviews-link"
							rightAdornment={
								!isEmpty(googleReviewsFeature?.properties ?? {}) && (
									<FeatureToggle
										isEnabled={!!googleReviewsFeature?.enabled}
										onChange={handleGoogleReviewsSwitchChange}
										ariaName="google reviews"
										trackingName="Google reviews"
										compact
									/>
								)
							}
						>
							Google reviews
						</FeatureLink>
					</Section>
				)}

				<Section>
					<SectionHeading>Integrations</SectionHeading>
					<FeatureLink
						href="/configurator/widget-content/helpdesk"
						icon={<HelpDeskLogoIcon />}
						rightAdornment={helpDeskIntegration?.connected && <EnabledBadge status="active">On</EnabledBadge>}
						disabled={isChatOnlyModeEnabled}
					>
						HelpDesk
					</FeatureLink>
					<FeatureLink
						isBeta
						href="/configurator/widget-content/livechat"
						icon={<LiveChatLogoIcon />}
						rightAdornment={
							liveChatIntegration?.connected && (
								<EnabledBadge status={liveChatIntegration.isLicenseActive ? 'active' : 'expired'}>
									{liveChatIntegration.isLicenseActive ? 'On' : 'Expired'}
								</EnabledBadge>
							)
						}
					>
						LiveChat
					</FeatureLink>
					<FeatureLink
						href="/configurator/widget-content/chatbot"
						icon={<ChatBotLogoIcon />}
						rightAdornment={
							chatbotIntegration?.connected && (
								<EnabledBadge status={chatbotIntegration.isExpired ? 'expired' : 'active'}>
									{chatbotIntegration.isExpired ? 'Expired' : 'On'}
								</EnabledBadge>
							)
						}
					>
						ChatBot
					</FeatureLink>
					<FeatureLink
						href="/configurator/widget-content/apps"
						icon={<AppsLogoIcon />}
						disabled={isChatOnlyModeEnabled}
					>
						Apps
					</FeatureLink>
				</Section>
			</Wrapper>
			<BottomShade />
		</SlideInWrapper>
	)
}

export default WidgetContentIndex
